import { RichText, RichTextValue } from "@/components/rich-text"
import { SlideOnView } from "@/components/slide-on-view"
import {
  AngularIcon,
  NextIcon,
  PythonIcon,
  ReactIcon,
  CSharpIcon,
  CursorIcon,
} from "@/svgs/brand-icons"
import { useInView } from "react-intersection-observer"
import { useScroll } from "motion/react"
import { HighlightAnimatedSvg } from "@/contents/new-blocks/fullstack/highlight-animated-svg"
import { SlidingWheel } from "@/contents/new-blocks/fullstack/sliding-wheel"
import { useRef, useState } from "react"
import { useBreakpoint } from "@/ui/hooks/useBreakpoint"
import { Cursor } from "@/components/cursor"
import Image from "next/image"

interface FullstackProps {
  content: {
    title: RichTextValue
    description: string
  }
}

export function Fullstack({ content }: FullstackProps) {
  const { breakpoint, windowWidth } = useBreakpoint()
  const [showCursor, setShowCursor] = useState(false)
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  })
  const containerRef = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"],
    axis: "y",
  })
  return (
    <div ref={containerRef} className="h-[200vh] w-full bg-primary-800">
      <div
        ref={ref}
        onMouseEnter={() => setShowCursor(true)}
        onMouseLeave={() => setShowCursor(false)}
        className="sticky top-0 left-0 flex h-screen justify-center overflow-hidden px-4 pt-5 text-neutral-light md:flex-row md:gap-24 md:px-16 md:py-30 lg:px-20 xl:px-35"
      >
        <Cursor color="viola" label="SCROLL" show={showCursor} />
        <div className="flex w-full max-w-lg flex-col justify-start gap-32 md:flex-row">
          <SlideOnView className="mmax-w-[60rem] relative z-20 md:pb-0">
            <RichText
              billboardClassName="text-neutral-light"
              value={content.title}
            />
            <div className="mt-10 text-lg md:mt-6 md:max-w-[46rem] lg:max-w-[62rem] xl:text-xlg">
              {content.description}
            </div>
          </SlideOnView>
          <div className="w-100" />
          <SlidingWheel
            progress={scrollYProgress}
            radiusX={
              breakpoint === "xl"
                ? 600
                : breakpoint === "lg"
                  ? 500
                  : breakpoint === "md"
                    ? 300
                    : windowWidth / 2
            }
            radiusY={breakpoint === "xl" ? 400 : 300}
            icons={[
              <PythonIcon className="size-15 md:size-20" />,
              <CursorIcon className="size-15 md:size-20" />,
              <ReactIcon className="size-15 md:size-20 lg:size-30" />,
              <NextIcon className="size-10" />,
              <AngularIcon className="size-20" />,
            ]}
            className="absolute top-3/4 right-0 z-10 translate-x-1/2 translate-y-[-56%] md:top-1/2 md:right-0 md:-translate-y-1/2"
          />
          <HighlightAnimatedSvg
            animate={inView}
            className="absolute top-3/4 right-1/2 z-0 h-[47rem] w-[47rem] translate-x-1/2 translate-y-[-60%] md:top-1/2 md:right-16 md:translate-x-0 md:-translate-y-1/2 lg:right-50 lg:h-[88rem] lg:w-[65rem] xl:right-70"
          />
        </div>
      </div>
    </div>
  )
}
